import { ClientApiError } from "@visioni/a3s-client-api/app";
import { ForbiddenError, UnauthorizedError } from "@src/features/auth/services/authenticated-fetch";
import { ApiError, ApiTimeoutError } from "@src/services/api/request";

/**
 * Why a request failed, as a fixed code for analytics. Never the error's message
 * or response body: both are free text that can echo user input.
 * - `unauthorized`: no session, or it expired (401).
 * - `forbidden`: the user may not do this (403).
 * - `client_error`: any other 4xx.
 * - `server_error`: a 5xx.
 * - `timeout`: the request timed out.
 * - `unknown`: anything else, network failures included.
 *
 * There is no `network` code: `apiRequest` and client-api mutations rethrow `fetch`'s own
 * `TypeError`, and from here it cannot be told apart from a `TypeError` thrown by a bug or
 * while reading the response. `API Error` (`apiRequest`) classifies at the fetch call and
 * does report `network_error`.
 */
export type RequestErrorType = "unauthorized" | "forbidden" | "client_error" | "server_error" | "timeout" | "unknown";

function statusErrorType(status: number): RequestErrorType {
  if (status === 401) return "unauthorized";
  if (status === 403) return "forbidden";
  if (status >= 400 && status < 500) return "client_error";
  if (status >= 500 && status < 600) return "server_error";
  return "unknown";
}

/** The {@link RequestErrorType} of an error thrown by `apiRequest` or a client-api mutation. */
export function requestErrorType(error: unknown): RequestErrorType {
  if (error instanceof UnauthorizedError) return "unauthorized";
  if (error instanceof ForbiddenError) return "forbidden";
  if (error instanceof ApiError || error instanceof ClientApiError) return statusErrorType(error.status);
  if (error instanceof ApiTimeoutError) return "timeout";
  return "unknown";
}
